import { useMemo, useCallback } from "react";
import { Corteva_Pages } from "@granular/authz";
import { useAtomValue } from "jotai";
import { isEmbeddedState } from "../state/mobile";
import { useFeatureFlags } from "./featureFlags";
import { useOperatingContext } from "./operatingContext";
import { useJwtUser } from "./useJwtUser";

/**
 * TODO: Temporary client-side aggregation of CARL (chat) visibility logic.
 * Replace once server-driven Feature Enablement / Entitlements service is live.
 *
 * Centralizes:
 *  - Feature flags:
 *      app-chatbot-disable-for-growers
 *  - Corteva tenant + email domain check
 *  - Permission gate: userCan("view", Corteva_Pages.corteva_page)
 *
 * Semantics:
 *  - If disable-for-growers flag is ON, only Corteva employees or users with the corteva_page view permission can see chat
 */
export type SeedMessage = {
  message: string;
  userEntered: boolean;
  hidden?: boolean;
};

export type OpenChatDataFilter = {
  id: string;
  display: string;
  type: string;
};

export type OpenChatOptions = {
  newChat?: boolean;
  seedMessages?: SeedMessage[];
  dataFilters?: OpenChatDataFilter[];
};

export type ShowChatState = {
  /**
   * Means user has access to view chat
   */
  canViewChat: boolean;
  /**
   * Means the chat button in the page context bar can be shown
   */
  canShowChatButton: boolean;
  /**
   * Has `...@corteva` email address
   */
  isCortevaUser: boolean;
  /**
   * Function to call to open the chat modal
   */
  openChat: (options?: OpenChatOptions) => void;
  /**
   * When true, CARL is hidden for all grower accounts
   */
  restrictGrowers: boolean;
};

/**
 * Shared hook to get CARL availability values
 */
export const useShowChat = (): ShowChatState => {
  const flags = useFeatureFlags() || {};
  const { userCan, allContexts } = useOperatingContext();
  const tokenClaims = useJwtUser();

  const isEmbedded = useAtomValue(isEmbeddedState);
  // Corteva tenant ID
  const cortevaTenantId = "3e20ecb2-9cb0-4df1-ad7b-914e31dcdda4";
  // Vylor tenant ID
  const vylorTenantId = "db505480-8ec6-495c-b579-32c58ed84a01";

  // If the user is impersonating, check the impersonation email first.
  const impersonationEmail =
    tokenClaims?.["custom:granular_impersonation_email"]?.toLowerCase() ?? "";
  const userEmail = tokenClaims?.email?.toLowerCase() ?? "";
  const emailToCheck = impersonationEmail || userEmail;

  const isInternalUser =
    ((tokenClaims?.iss ?? "").includes(cortevaTenantId) &&
      /^[^@]+@corteva\.com$/.test(emailToCheck)) ||
    ((tokenClaims?.iss ?? "").includes(vylorTenantId) &&
      /^[^@]+@vylor\.com$/.test(emailToCheck));

  // For backward compatibility
  const isCortevaUser =
    (tokenClaims?.iss ?? "").includes(cortevaTenantId) &&
    /^[^@]+@corteva\.com$/.test(emailToCheck);

  const restrictGrowers = !!flags["app-chatbot-disable-for-growers"];
  const isMobileAskCarlEnabled = !!flags["app-chatbot-enable-mobile-ask-carl"];

  const canViewChat = restrictGrowers
    ? isInternalUser || userCan("view", Corteva_Pages.corteva_page)
    : true;

  // Disable the "Ask Carl" button in the mobile embedded experience if the feature flag is false.
  const askCarlDisableForMobile = isEmbedded && !isMobileAskCarlEnabled;

  // List of conditions for showing the chat button:
  // - User can view chat (passes permission and tenant checks)
  // - There is at least one operating context available
  // - Not in embedded mobile with Ask Carl disabled
  const canShowChatButton =
    canViewChat && allContexts?.length > 0 && !askCarlDisableForMobile;

  const openChat = useCallback((options?: OpenChatOptions) => {
    const event = new CustomEvent("openChat", {
      detail: { open: true, ...options },
    });
    globalThis.dispatchEvent(event);
  }, []);

  return useMemo(
    () => ({
      canViewChat,
      canShowChatButton,
      isCortevaUser,
      restrictGrowers,
      openChat,
    }),
    [canViewChat, canShowChatButton, isCortevaUser, restrictGrowers, openChat],
  );
};
